import React from 'react';
import Layout from '../../../shared/components/Layout';
import {
  Target,
  Shield,
  Heart,
  Users,
  Zap,
  Award,
  CheckCircle,
  Smartphone,
  Globe,
} from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <Layout>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                关于跑伴
              </h1>
              <p className="text-xl md:text-2xl text-orange-100">
                专业陪跑服务平台，让每一次奔跑都有专业陪伴
              </p>
            </div>
          </div>
        </section>

        {/* 产品介绍 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  我们的产品
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  跑伴是一个连接跑者与专业陪跑者（私兔）的平台，通过创新的服务模式，为跑步爱好者提供专业、安全、有趣的陪跑服务
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8">
                  <Target className="w-12 h-12 text-orange-500 mb-4" />
                  <h3 className="text-xl font-bold mb-3">专业配速</h3>
                  <p className="text-gray-600">
                    经验丰富的陪跑员，精准控制配速，助你达成目标时间，提升跑步表现
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                  <Shield className="w-12 h-12 text-blue-500 mb-4" />
                  <h3 className="text-xl font-bold mb-3">安全保障</h3>
                  <p className="text-gray-600">
                    专业认证陪跑员，熟悉路线安全，提供应急处理，让你安心享受跑步乐趣
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8">
                  <Heart className="w-12 h-12 text-green-500 mb-4" />
                  <h3 className="text-xl font-bold mb-3">心理支持</h3>
                  <p className="text-gray-600">
                    专业心理疏导，克服跑步焦虑，提供正向激励，让你爱上跑步运动
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 核心功能 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                核心功能
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                {/* 跑者功能 */}
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold">跑者功能</h3>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>发布招募需求，描述目标和要求</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>浏览私兔档案，选择合适的陪跑员</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>在线支付订单，安全便捷</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>服务完成后评价，帮助其他跑者</span>
                    </li>
                  </ul>
                </div>

                {/* 私兔功能 */}
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold">私兔功能</h3>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>发布个人档案，展示专业能力</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>申请招募任务，获得服务机会</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>管理订单状态，跟踪服务进度</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span>创作内容获得奖励，提升收入</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 技术特色 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                技术特色
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">微信小程序</h3>
                  <p className="text-gray-600">
                    原生小程序开发，流畅体验，支持微信支付和登录
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">多主题系统</h3>
                  <p className="text-gray-600">
                    10种预设主题，支持动态切换，个性化用户体验
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">RESTful API</h3>
                  <p className="text-gray-600">
                    完整的API接口，支持第三方集成和扩展
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 数据统计 */}
        <section className="py-16 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                平台数据
              </h2>

              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white bg-opacity-10 rounded-2xl p-6 text-center backdrop-blur-sm">
                  <div className="text-4xl font-bold mb-2">2000+</div>
                  <div className="text-orange-100">专业私兔</div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-2xl p-6 text-center backdrop-blur-sm">
                  <div className="text-4xl font-bold mb-2">120+</div>
                  <div className="text-orange-100">覆盖赛事</div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-2xl p-6 text-center backdrop-blur-sm">
                  <div className="text-4xl font-bold mb-2">1200+</div>
                  <div className="text-orange-100">完成订单</div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-2xl p-6 text-center backdrop-blur-sm">
                  <div className="text-4xl font-bold mb-2">98%</div>
                  <div className="text-orange-100">满意度</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 独家优势 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                独家优势
              </h2>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <Award className="w-12 h-12 text-orange-500 mr-4" />
                  <div>
                    <h3 className="text-2xl font-bold">元大都马拉松冠军俱乐部</h3>
                    <p className="text-gray-600">国内最大的民间跑步俱乐部</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold mb-3">资源优势</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li>• 全国2000+专业会员</li>
                      <li>• 覆盖全国主要城市</li>
                      <li>• 马拉松精英选手和专业教练</li>
                      <li>• 独家资源，竞争对手难以复制</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold mb-3">服务保障</h4>
                    <ul className="space-y-2 text-gray-600">
                      <li>• 无需从零招募，启动快</li>
                      <li>• 私兔质量有保证，用户信任度高</li>
                      <li>• 规模化能力强，可快速扩张</li>
                      <li>• 专业培训体系，持续提升服务质量</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AboutPage;
